/* eslint-disable max-len */

import { BamCommercialFamilyCodesFileResponse } from '@1po/1po-bff-fe-spec/generated/backoffice/bam_commercial_family_codes/response/BamCommercialFamilyCodesFileResponse';
import {
  BACKOFFICE_BAM_COMMERCIAL_FAMILY_CODES_DELETE_FILE_RESPONSE,
  BACKOFFICE_BAM_COMMERCIAL_FAMILY_CODES_GET_FILE_RESPONSE,
  BACKOFFICE_BAM_COMMERCIAL_FAMILY_CODES_UPLOAD_FILE_RESPONSE,
} from '@1po/1po-bff-fe-spec/generated/common/ResponseType';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RootState } from 'app/AppStore';
import { FETCHING_FILE, ModalStatusType, NO_FILE } from 'components/FileUpload/paramsByState';

import {
  BAM_COMMERCIAL_FAMILY_CODES_NAMESPACE,
  BamCommercialFamilyCodesFileInformation,
  BamCommercialFamilyCodesFileStatus,
  BamCommercialFamilyCodesState,
  DELETE_FILE,
  GET_FILE,
  UPLOAD_FILE,
} from 'domains/bamCommercialFamilyCodes/BamCommercialFamilyCodes.types';

import { SEARCH_STATUS } from 'utils';

// Init state
const initialState: BamCommercialFamilyCodesState = {
  fileUploadStatus: {
    fileStatus: FETCHING_FILE,
    modalStatus: NO_FILE,
  },
  bamCommercialFamilyCodes: [],
  fileInformation: { fileName: '' },
  fileInformationForRequest: { fileName: '' },
  searchStatus: undefined,
};

// Saga actions
export const uploadFileBamCommercialFamilyCodesRequestSaga = createAction<{
  fileName: string;
  fileBase64: string;
}>(UPLOAD_FILE);
export const uploadFileBamCommercialFamilyCodesResponseSaga = createAction(
  BACKOFFICE_BAM_COMMERCIAL_FAMILY_CODES_UPLOAD_FILE_RESPONSE,
);

export const fetchFileBamCommercialFamilyCodesRequestSaga = createAction(GET_FILE);
export const fetchFileBamCommercialFamilyCodesResponseSaga = createAction(
  BACKOFFICE_BAM_COMMERCIAL_FAMILY_CODES_GET_FILE_RESPONSE,
);

export const deleteFileBamCommercialFamilyCodesRequestSaga = createAction(DELETE_FILE);
export const deleteFileBamCommercialFamilyCodesResponseSaga = createAction(
  BACKOFFICE_BAM_COMMERCIAL_FAMILY_CODES_DELETE_FILE_RESPONSE,
);

// Slice
const slice = createSlice({
  name: BAM_COMMERCIAL_FAMILY_CODES_NAMESPACE,
  initialState,
  reducers: {
    setBamCommercialFamilyCodesFileUploadStatus: (
      state,
      { payload }: PayloadAction<BamCommercialFamilyCodesFileStatus>,
    ) => {
      state.fileUploadStatus.fileStatus = payload;
    },
    setBamCommercialFamilyCodesFileModalStatus: (state, { payload }: PayloadAction<ModalStatusType>) => {
      state.fileUploadStatus.modalStatus = payload;
    },
    setBamCommercialFamilyCodesFileInformationForRequest: (
      state,
      { payload }: PayloadAction<BamCommercialFamilyCodesFileInformation>,
    ) => {
      state.fileInformationForRequest = payload;
    },
    setBamCommercialFamilyCodesSearchFileStatus: (state, { payload }: PayloadAction<SEARCH_STATUS>) => {
      state.searchStatus = payload;
    },
    setBamCommercialFamilyCodesFileResponse: (
      state,
      { payload }: PayloadAction<BamCommercialFamilyCodesFileResponse>,
    ) => {
      const { fileName, fileRows } = payload;
      state.fileInformation = {
        fileName,
      };
      state.bamCommercialFamilyCodes = fileRows;
    },
  },
});

export const {
  setBamCommercialFamilyCodesFileModalStatus,
  setBamCommercialFamilyCodesFileInformationForRequest,
  setBamCommercialFamilyCodesFileUploadStatus,
  setBamCommercialFamilyCodesSearchFileStatus,
  setBamCommercialFamilyCodesFileResponse,
} = slice.actions;

export const getBamCommercialFamilyCodesFileInformationForRequest = createSelector(
  (state: RootState) => state.bamCommercialFamilyCodes.fileInformationForRequest,
  (fileInformation) => fileInformation,
);

export const getBamCommercialFamilyCodesFileInformation = createSelector(
  (state: RootState) => state.bamCommercialFamilyCodes.fileInformation,
  (fileInformation) => fileInformation,
);

export const getBamCommercialFamilyCodesFileUploadStatus = createSelector(
  (state: RootState) => state.bamCommercialFamilyCodes.fileUploadStatus.fileStatus,
  (fileStatus) => fileStatus,
);

export const getBamCommercialFamilyCodesFileModalStatus = createSelector(
  (state: RootState) => state.bamCommercialFamilyCodes.fileUploadStatus.modalStatus,
  (modalStatus) => modalStatus,
);

export const getBamCommercialFamilyCodesFileFetchStatus = createSelector(
  (state: RootState) => state.bamCommercialFamilyCodes.searchStatus,
  (searchStatus) => searchStatus,
);

export const getBamCommercialFamilyCodes = createSelector(
  (state: RootState) => state.bamCommercialFamilyCodes.bamCommercialFamilyCodes,
  (bamCommercialFamilyCodes) => bamCommercialFamilyCodes,
);

export default slice.reducer;
